import React, { useState, useEffect } from 'react';
import { Clock, ArrowUpRight, ArrowDownLeft, CreditCard, Filter, ChevronDown, ChevronUp, Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../config/api';

const TransactionHistory = () => {
  const { currentUser, getIdToken } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterType, setFilterType] = useState('all'); // 'all', 'purchased', 'sent', 'received'
  const [sortOrder, setSortOrder] = useState('desc'); // 'desc' or 'asc'
  const [showFilters, setShowFilters] = useState(false);;
  
  useEffect(() => {
    fetchTransactionHistory();
  }, [currentUser, filterType, sortOrder]);

  const fetchTransactionHistory = async () => {
    if (!currentUser) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const token = await getIdToken();
      
      if (!token) {
        throw new Error('Authentication required');
      }
      
      // Construct the query with filters
      let url = `${API_BASE_URL}/user/transactions?sort=${sortOrder}`;
      if (filterType !== 'all') {
        url += `&type=${filterType}`;
      }
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setTransactions(data);
    } catch (error) {
      console.error('Failed to fetch transaction history:', error);
      setError('Failed to load transaction history');
    } finally {
      setLoading(false);
    }
  };
  
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    // If timestamp is a Firestore timestamp object
    if (timestamp.seconds) {
      const date = new Date(timestamp.seconds * 1000);
      return date.toLocaleString();
    }
    
    // If timestamp is a string in YYYYMMDDHHmmSS format
    if (typeof timestamp === 'string' && timestamp.length === 14) {
      const year = timestamp.substring(0, 4);
      const month = timestamp.substring(4, 6);
      const day = timestamp.substring(6, 8);
      const hour = timestamp.substring(8, 10);
      const minute = timestamp.substring(10, 12);
      const second = timestamp.substring(12, 14);
      
      return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
    }
    
    // If timestamp is a regular date string
    return new Date(timestamp).toLocaleString();
  };
  
  const getTransactionIcon = (type) => {
    switch (type) {
      case 'purchased':
      case 'admin_add':
        return <CreditCard className="w-4 h-4 text-green-500" />;
      case 'sent':
      case 'user_transfer_out':
        return <ArrowUpRight className="w-4 h-4 text-red-500" />;
      case 'received':
      case 'user_transfer_in':
        return <ArrowDownLeft className="w-4 h-4 text-blue-500" />;
      default:
        return <Clock className="w-4 h-4 text-gray-500" />;
    }
  };
  
  const getTransactionTypeLabel = (type) => {
    switch (type) {
      case 'purchased':
        return 'Purchased';
      case 'admin_add':
        return 'Added by Admin';
      case 'user_transfer_out':
        return 'Sent';
      case 'user_transfer_in':
        return 'Received';
      case 'event_entry':
        return 'Event Entry';
      default:
        return type;
    }
  };
  
  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case 'success':
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-300';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow p-6 mt-8">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-gray-800">
          Transaction History
        </h2>
        
        <div className="flex gap-2">
          <button 
            onClick={() => setShowFilters(!showFilters)} 
            className="flex items-center px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
          >
            <Filter className="w-4 h-4 mr-1" />
            Filters
            {showFilters ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </button>
          
          <button
            onClick={() => setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc')}
            className="flex items-center px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50"
          >
            {sortOrder === 'desc' ? (
              <>Newest First <ChevronDown className="w-4 h-4 ml-1" /></>
            ) : (
              <>Oldest First <ChevronUp className="w-4 h-4 ml-1" /></>
            )}
          </button>
        </div>
      </div>
      
      {showFilters && (
        <div className="flex flex-wrap gap-2 mb-4 p-3 bg-gray-50 rounded-lg">
          <button
            onClick={() => setFilterType('all')}
            className={`px-3 py-1.5 text-sm rounded-lg ${
              filterType === 'all' ? 'bg-blue-100 text-blue-700' : 'bg-white border'
            }`}
          >
            All
          </button>
          <button
            onClick={() => setFilterType('purchased')}
            className={`px-3 py-1.5 text-sm rounded-lg ${
              filterType === 'purchased' ? 'bg-green-100 text-green-700' : 'bg-white border'
            }`}
          >
            Purchased
          </button>
          <button
            onClick={() => setFilterType('sent')}
            className={`px-3 py-1.5 text-sm rounded-lg ${
              filterType === 'sent' ? 'bg-red-100 text-red-700' : 'bg-white border'
            }`}
          >
            Sent
          </button>
          <button
            onClick={() => setFilterType('received')}
            className={`px-3 py-1.5 text-sm rounded-lg ${
              filterType === 'received' ? 'bg-blue-100 text-blue-700' : 'bg-white border'
            }`}
          >
            Received
          </button>
        </div>
      )}
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {loading ? (
        <div className="flex justify-center items-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600 mr-2" />
          <span>Loading transaction history...</span>
        </div>
      ) : transactions.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No transaction history found.</p>
          {filterType !== 'all' && (
            <p className="mt-2 text-sm">Try changing your filter settings.</p>
          )}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date/Time
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Transaction
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Details
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {transactions.map((transaction) => (
                <tr key={transaction.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(transaction.timestamp)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0">
                        {getTransactionIcon(transaction.type)}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {getTransactionTypeLabel(transaction.type)}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`text-sm font-medium ${
                      transaction.type === 'sent' || transaction.type === 'user_transfer_out' 
                        ? 'text-red-600' 
                        : 'text-green-600'
                    }`}>
                      {transaction.type === 'sent' || transaction.type === 'user_transfer_out' 
                        ? `-${transaction.credits}` 
                        : `+${transaction.credits}`} credits
                    </div>
                    {transaction.amount_usd && (
                      <div className="text-xs text-gray-500">
                        USD {(transaction.amount_usd / 100).toFixed(2)}
                      </div>
                    )}
                    {transaction.amount_zar && (
                      <div className="text-xs text-gray-500">
                        ZAR {(transaction.amount_zar / 100).toFixed(2)}
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(transaction.status)}`}>
                      {transaction.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {transaction.reference && (
                      <div className="text-xs">Ref: {transaction.reference.substring(0, 8)}...</div>
                    )}
                    {transaction.sender_email && (
                      <div className="text-xs">From: {transaction.sender_email}</div>
                    )}
                    {transaction.recipient_email && (
                      <div className="text-xs">To: {transaction.recipient_email}</div>
                    )}
                    {transaction.package && (
                      <div className="text-xs">Package: {transaction.package}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;